import { z } from 'zod';
import { RequestReturnSnapshotPayloadSchema } from './return-snapshot-payload.js';

/**
 * @aiPurpose list-item projection of a `RequestReturnSnapshot` row returned by
 *   `GET /requests/:id/snapshots` (epic #2099 / #2138). Header metadata only —
 *   the heavy frozen `payload` JSON is fetched per-row via the detail endpoint.
 *   `returnedAt` follows the sibling read-schema convention (`z.string().
 *   datetime()`, the backend maps the Prisma `Date` via `.toISOString()`).
 * @aiSecurity the internal `photoObjectKeys` download-authorization metadata is
 *   NEVER part of this (or the detail) read shape — only the photo-download
 *   endpoint consults it.
 */
export const RequestReturnSnapshotListItemSchema = z
  .object({
    id: z.string().uuid(),
    returnedAt: z.string().datetime(),
    returnedById: z.string().uuid(),
    reason: z.string(),
  })
  .strict();
export type RequestReturnSnapshotListItem = z.infer<typeof RequestReturnSnapshotListItemSchema>;

/**
 * @aiPurpose detail projection returned by
 *   `GET /requests/:id/snapshots/:snapshotId` (epic #2099 / #2138): the list
 *   fields plus the frozen `payload`. `payload` reuses #2135's strict
 *   {@link RequestReturnSnapshotPayloadSchema} VERBATIM, so the read surface
 *   inherits that schema's exclusions (no files/chat/audit/financials/
 *   `clientToken`/`leadId`/`dealId`/carrier `insuranceQuotes`).
 * @aiSecurity do NOT widen `payload` to a passthrough/`z.any()`/the full
 *   request-read schema — that would re-expose the data #2135 deliberately
 *   froze out.
 */
export const RequestReturnSnapshotDetailSchema = z
  .object({
    id: z.string().uuid(),
    returnedAt: z.string().datetime(),
    returnedById: z.string().uuid(),
    reason: z.string(),
    payload: RequestReturnSnapshotPayloadSchema,
  })
  .strict();
export type RequestReturnSnapshotDetail = z.infer<typeof RequestReturnSnapshotDetailSchema>;
